import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ProjectDetail } from "../types";

type PermitAsset = ProjectDetail["documentAssets"][number];

export function PermitAssetCarousel({ assets, address }: { assets: PermitAsset[]; address: string }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const lightboxSlidesRef = useRef<HTMLDivElement>(null);
  const scrollEndTimer = useRef<number | undefined>(undefined);
  const closeFullScreen = () => setIsFullScreen(false);

  useEffect(() => {
    if (!isFullScreen) return;
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") closeFullScreen(); };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  if (!assets.length) return null;

  const asset = assets[Math.min(activeIndex, assets.length - 1)];
  const title = asset.sheetTitle || "Filed permit sheet";
  const showPrevious = () => setActiveIndex((index) => (index - 1 + assets.length) % assets.length);
  const showNext = () => setActiveIndex((index) => (index + 1) % assets.length);

  useEffect(() => {
    if (!isFullScreen) return;
    const frame = requestAnimationFrame(() => {
      const slides = lightboxSlidesRef.current;
      slides?.scrollTo({ left: slides.clientWidth * activeIndex });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeIndex, isFullScreen]);

  useEffect(() => () => window.clearTimeout(scrollEndTimer.current), []);

  return <section className="permit-asset-carousel" aria-label={`Filed permit assets for ${address}`}>
    <div className="permit-asset-frame">
      <button type="button" className="permit-asset-open" onClick={() => setIsFullScreen(true)} aria-label={`View ${title} for ${address} full screen`}>
        <img src={asset.url} alt={`${title} for ${address}, filed permit page ${asset.sourcePage}`} />
      </button>
      {assets.length > 1 ? <>
        <button type="button" className="permit-asset-control previous" onClick={showPrevious} aria-label="Previous filed asset"><ChevronLeft /></button>
        <button type="button" className="permit-asset-control next" onClick={showNext} aria-label="Next filed asset"><ChevronRight /></button>
      </> : null}
    </div>
    <div className="permit-asset-caption">
      <div><strong>{title}</strong><span>Filed permit · page {asset.sourcePage}</span></div>
      <span aria-live="polite">{activeIndex + 1} / {assets.length}</span>
    </div>
    {assets.length > 1 ? <div className="permit-asset-dots" aria-label="Choose a filed permit asset">
      {assets.map((item, index) => <button
        type="button"
        key={item.url}
        className={`permit-asset-dot${index === activeIndex ? " active" : ""}`}
        onClick={() => setActiveIndex(index)}
        aria-label={`Show ${item.sheetTitle || "filed permit sheet"}, page ${item.sourcePage}`}
        aria-current={index === activeIndex ? "true" : undefined}
      />)}
    </div> : null}
    {isFullScreen ? <div className="permit-asset-lightbox" role="dialog" aria-modal="true" aria-label={`Filed permit sheets for ${address}`}>
      <div className="permit-asset-lightbox-content">
        <button type="button" className="permit-asset-lightbox-close" onClick={closeFullScreen} aria-label="Close full-screen permit asset"><X /></button>
        <div
          className="permit-asset-lightbox-slides"
          ref={lightboxSlidesRef}
          onScroll={(event) => {
            const { scrollLeft, clientWidth } = event.currentTarget;
            window.clearTimeout(scrollEndTimer.current);
            scrollEndTimer.current = window.setTimeout(() => {
              const nextIndex = Math.round(scrollLeft / clientWidth);
              setActiveIndex(nextIndex);
            }, 100);
          }}
        >
          {assets.map((item) => <div className="permit-asset-lightbox-slide" key={item.url}>
            <img src={item.url} alt={`${item.sheetTitle || "Filed permit sheet"} for ${address}, filed permit page ${item.sourcePage}`} />
          </div>)}
        </div>
        {assets.length > 1 ? <>
          <button type="button" className="permit-asset-lightbox-control previous" onClick={showPrevious} aria-label="Previous filed permit sheet"><ChevronLeft /></button>
          <button type="button" className="permit-asset-lightbox-control next" onClick={showNext} aria-label="Next filed permit sheet"><ChevronRight /></button>
        </> : null}
        <p>{title} · Filed permit page {asset.sourcePage} <span aria-live="polite">({activeIndex + 1} of {assets.length})</span></p>
      </div>
    </div> : null}
  </section>;
}
